import { useEffect } from 'react';

export default function Terms() {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="legal">
            <div className="container">
                <h1>Terms &amp; Conditions – CloTech AI Pvt. Ltd. (CloTech AI CRM)</h1>
                <p><strong>Effective Date:</strong> 18.09.2025<br /><strong>Last Updated:</strong> 18.09.2025</p>
                <p>These Terms &amp; Conditions (“Terms”) govern your access to and use of the CloTech AI CRM software and related websites, services, and integrations (collectively, the “Services”) provided by CloTech AI Pvt. Ltd. (“CloTech AI”, “we”, “our”, “us”). By creating an account, accessing, or using the Services, you agree to be bound by these Terms.</p>
                <hr />
                <h2>1. Eligibility &amp; Account</h2>
                <ul>
                    <li>You must be 18+ and authorized to bind your organization.</li>
                    <li>You’re responsible for all activity under your account, including actions by users you add.</li>
                    <li>Keep credentials confidential; notify us immediately of suspected unauthorized use.</li>
                </ul>
                <hr />
                <h2>2. Subscription Plans &amp; Scope of Use</h2>
                <ul>
                    <li>The Services are licensed on a subscription basis according to your selected plan (e.g., Buds, Bloom, Blossom) and any add-ons (users, branches, modules, messaging packs).</li>
                    <li>Plan limits and available features are defined in your order form or within the application.</li>
                    <li>Unless expressly permitted, you must not:
                        <ul>
                            <li>copy, modify, reverse engineer, or create derivative works of the Services;</li>
                            <li>resell, sublicense, or provide the Services to third parties (except under a separate partner agreement).</li>
                        </ul>
                    </li>
                </ul>
                <hr />
                <h2>3. Add-Ons: Users, Branches, Messaging</h2>
                <ul>
                    <li>Additional users/branches can be added anytime and are billed pro-rata for the current term.</li>
                    <li>Messaging (SMS/Email/WhatsApp): plans may include limited freebies for the first month; additional usage is billed at prevailing rates. Delivery is dependent on third-party carriers/gateways; undelivered messages are not guaranteed or refunded.</li>
                    <li>You are responsible for lawful use of messaging (see §10 Compliance).</li>
                </ul>
                <hr />
                <h2>4. Billing, Taxes &amp; Renewal</h2>
                <ul>
                    <li>Subscriptions are billed monthly or annually (as chosen). Taxes are extra where applicable.</li>
                    <li>Subscriptions auto-renew for the same term unless cancelled before the renewal date in your admin panel or via a written request.</li>
                    <li>Late or failed payments may result in suspension or termination of Services.</li>
                    <li>Prices may change upon renewal; we’ll notify you in advance where required.</li>
                </ul>
                <hr />
                <h2>5. Setup, Onboarding &amp; Support</h2>
                <ul>
                    <li>We provide onboarding guidance, configuration, and training; setup/hand-holding fees may apply based on scope.</li>
                    <li>Support channels: chat/email (Buds &amp; Bloom), and priority support (chat/email/optional voice) for Blossom.</li>
                    <li>WhatsApp support is available for convenience but not guaranteed as a 24×7 channel.</li>
                </ul>
                <hr />
                <h2>6. Data Ownership, Processing &amp; Backups</h2>
                <ul>
                    <li>You own your data. You grant CloTech AI a limited license to process it solely to provide the Services.</li>
                    <li>We implement administrative, technical, and organizational measures to protect your data (see our <a href="/privacy-policy">Privacy Policy</a>).</li>
                    <li>We perform periodic backups; however, you are responsible for exporting your data as needed for your records.</li>
                </ul>
                <hr />
                <h2>7. Third-Party Services &amp; Integrations</h2>
                <ul>
                    <li>The Services may integrate with third-party tools (e.g., Email, SMS, WhatsApp, payment gateways, APIs).</li>
                    <li>Your use of third-party services is governed by their terms/privacy policies. We are not liable for third-party availability, performance, or security.</li>
                </ul>
                <hr />
                <h2>8. Acceptable Use</h2>
                <p>You agree not to use the Services to:</p>
                <ul>
                    <li>store or transmit unlawful, harmful, defamatory, or infringing content;</li>
                    <li>send spam, phishing, or unsolicited messages;</li>
                    <li>attempt to breach, probe, or disrupt the Service or other accounts;</li>
                    <li>violate privacy, intellectual property, or any applicable law.</li>
                </ul>
                <p>We may suspend accounts for suspected violations.</p>
                <hr />
                <h2>9. Intellectual Property</h2>
                <ul>
                    <li>The Services, software, documentation, and all related IP are owned by CloTech AI or its licensors.</li>
                    <li>No rights are granted except the limited subscription license described here.</li>
                    <li>You may provide feedback; we may use it without obligation, and it does not create any IP rights in the Services for you.</li>
                </ul>
                <hr />
                <h2>10. Compliance (India: TRAI/DLT, Anti-Spam, Data Laws)</h2>
                <ul>
                    <li>You must ensure your messaging use complies with TRAI/DLT regulations and any applicable anti-spam/consent laws.</li>
                    <li>If you collect personal data, you are responsible for lawful processing under applicable laws (e.g., India’s DPDP Act 2023, GDPR if applicable).</li>
                    <li>On request, we can execute a data processing addendum for regulated customers.</li>
                </ul>
                <hr />
                <h2>11. Service Availability, Updates &amp; Beta Features</h2>
                <ul>
                    <li>We aim for high availability but do not guarantee uninterrupted or error-free operation.</li>
                    <li>We may release updates or improvements from time to time.</li>
                    <li>Access to beta/experimental features is provided “as-is” and may be modified or discontinued.</li>
                </ul>
                <hr />
                <h2>12. Term &amp; Termination</h2>
                <ul>
                    <li>These Terms remain in effect while you use the Services.</li>
                    <li>You may terminate at the end of your current term by cancelling before renewal.</li>
                    <li>We may terminate or suspend access for material breach, non-payment, or unlawful use.</li>
                    <li>Upon termination, your access ends; export your data before termination. We may retain limited data as required by law or for legitimate business purposes (see <a href="/privacy-policy">Privacy Policy</a>).</li>
                </ul>
                <hr />
                <h2>13. Refunds &amp; Cancellations</h2>
                <ul>
                    <li>Subscriptions are non-refundable except as expressly stated in our <a href="/cancellation-refund-policy">Cancellation &amp; Refund Policy</a>.</li>
                    <li>Upgrades are billed pro-rata; downgrades take effect in the next term.</li>
                </ul>
                <hr />
                <h2>14. Warranties &amp; Disclaimers</h2>
                <ul>
                    <li>The Services are provided “as-is” and “as-available.”</li>
                    <li>We disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</li>
                </ul>
                <hr />
                <h2>15. Limitation of Liability</h2>
                <ul>
                    <li>To the maximum extent permitted by law, CloTech AI is not liable for indirect, incidental, special, consequential, or punitive damages, or loss of profits, revenue, or data.</li>
                    <li>Our total aggregate liability for any claim in a subscription term will not exceed the fees paid by you to CloTech AI for that term.</li>
                </ul>
                <hr />
                <h2>16. Indemnity</h2>
                <p>You agree to indemnify and hold harmless CloTech AI and its officers, employees, and agents from any claims, damages, liabilities, costs, and expenses arising from: (a) your content or data; (b) your use of the Services; (c) your breach of these Terms; or (d) your violation of law or third-party rights.</p>
                <hr />
                <h2>17. Changes to Terms</h2>
                <p>We may update these Terms from time to time. The updated version will be posted with a revised “Last Updated” date. Continued use after changes constitutes acceptance.</p>
                <hr />
                <h2>18. Governing Law &amp; Dispute Resolution</h2>
                <ul>
                    <li>These Terms are governed by the laws of India.</li>
                    <li>Courts at Kolkata, West Bengal shall have exclusive jurisdiction.</li>
                    <li>Parties will first attempt to resolve disputes amicably; failing which, disputes may be referred to arbitration in Kolkata in accordance with the Arbitration and Conciliation Act, 1996 (single arbitrator, English language).</li>
                </ul>
                <hr />


                <h2>19. Contact &amp; Communication Address</h2>

                <p>
                    For any questions or concerns regarding these Terms, please <a href="/contact">contact us</a>.
                </p>

            </div>
        </div>
    );
}
